import { useState } from "react";
import type { ButtonProps } from "@hightouch/formkit";
import type { SharedProps } from ".";
import {
  Box,
  Button,
  ButtonGroup,
  DownloadIcon,
  IconButton,
  Tooltip,
} from "@hightouch/htui";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { graphQLFetch } from "src/formkit/hooks";
import { LinkButton } from "src/router";

export const FKButton = ({
  query,
  label,
  url,
  isDisabled,
  ...args
}: ButtonProps & { onClickUrlQuery: any } & SharedProps) => {
  const [isLoading, setIsLoading] = useState(false);

  query.data = query.data ?? "";
  const generatedUrl = url
    ? typeof url === "string"
      ? url
      : query.data
    : undefined;
  const { destination, model } = useFormkitContext();
  if (args.mode === "link" && (generatedUrl || args.onClickUrlQuery)) {
    return (
      <Button
        isDisabled={isDisabled || isLoading}
        isLoading={isLoading}
        variant={args.variant || "primary"}
        onClick={async () => {
          if (isLoading) return;

          let onClickUrl;
          // For the edge case in which the page rerenders and reruns the above GraphQL query, causing credentials to be reset in OAuth
          // i.e. Google Sheets SA
          if (args.onClickUrlQuery) {
            setIsLoading(true);
            try {
              const data = await graphQLFetch({
                destinationId: destination?.id,
                query: args.onClickUrlQuery.query,
                modelId: model?.id,
                variables: args.onClickUrlQuery.variables,
              });
              onClickUrl = data;
            } finally {
              setIsLoading(false);
            }
          }
          if (args.mode === "link" && args.newTab) {
            window?.open(generatedUrl || onClickUrl, "_blank", "noreferrer");
          } else {
            location.href = generatedUrl || onClickUrl;
          }
        }}
      >
        {label}
      </Button>
    );
  } else if (args.mode === "mailto" && generatedUrl) {
    return (
      <Button
        isDisabled={isDisabled || isLoading}
        isLoading={isLoading}
        variant={args.variant || "primary"}
      >
        <Box
          as="a"
          href={generatedUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </Box>
      </Button>
    );
  } else if (args.mode === "download" && generatedUrl) {
    return (
      <ButtonGroup>
        <Tooltip message={`Download ${args.filename}`}>
          <a
            href={generatedUrl}
            download={args.filename}
            rel="noopener"
            target="_blank"
          >
            <IconButton
              aria-label={args.filename}
              icon={DownloadIcon}
              size="sm"
              isDisabled={isDisabled || isLoading}
              onClick={() => {}}
            />
          </a>
        </Tooltip>
      </ButtonGroup>
    );
  }

  return (
    <>
      {args.mode === "link" && (
        <LinkButton href={generatedUrl}>{label}</LinkButton>
      )}
    </>
  );
};
